import { toCanvasTrackball } from '../geometry/trackball';
import {
  NUMBER_AXIS_SCALES,
  toAxisLabels,
  type AxisLabels,
} from '../data/axisLabels';
import type { Size } from '../types';
import type {
  AxisScales,
  CameraState,
  CoincidentAggregation,
  GridResolution,
  HeightAxis,
  Isoline,
  ScalarGrid,
  XyzDataPoint,
  ZInterpolation,
} from '../types';
import {
  collapseCoincident,
  type CollapsedSample,
} from '../data/collapseCoincident';
import { toColumnId, toSampleId } from '../data/markIds';
import { isSameResolution, toScalarGrid } from '../data/toScalarGrid';
import { toAdaptiveMesh } from '../geometry/adaptiveMesh';
import { toIsolines, toThresholds } from '../geometry/marchingSquares';
import { createProjector, DEFAULT_CAMERA } from '../geometry/projection';
import {
  findColumnAt,
  toBinnedColumns,
  toColumnBoxes,
  toColumnHeightRange,
  toColumnSample,
  toPaintingOrder,
  toViewer,
  type BinnedColumn,
  type ColumnBox,
} from '../geometry/binnedColumns';
import {
  EMPTY_MESH,
  rescaleNormals,
  toFacetedMesh,
  toUniformMesh,
  type SurfaceMesh,
} from '../geometry/surfaceMesh';
import { isSameCamera } from '../interaction/orbitCamera';
import {
  hasKeyboardRotationEnded,
  hasKeyboardRotationStarted,
  hasKeyboardZoomEnded,
  initialOrbitGestureState,
  isOrbitButton,
  isRotating,
  isSustainedRotation,
  reduceOrbitGesture,
  toKeyDownInput,
  toKeyUpInput,
  toWheelInput,
  type OrbitGestureState,
  type OrbitInput,
} from '../interaction/orbitGestures';
import {
  createColorScale,
  type ColorScale,
  type SequentialRampName,
} from '../render/colorScale';
import { DEFAULT_LIGHTING, type LightingModel } from '../render/lighting';
import { renderAxes3d } from '../render/renderAxes3d';
import {
  AXIS_BOX_THICKNESS,
  axisBoxBase,
  axisBoxBounds,
} from '../render/axisBox';
import { niceAxis } from '@ardoq/math';
import {
  DEFAULT_AXIS_TICK_COUNT,
  type HorizontalAxes,
  type HorizontalAxis,
  toLatticeFrame,
  toTickInset,
  toTickStep,
} from '../data/horizontalAxes';
import { CHROME_INSET, fitZoom, type UnitBounds } from '../geometry/fitZoom';
import { clampZoom } from '../interaction/orbitCamera';
import { renderAxisBox, renderCrosshair3d } from '../render/renderAxisBox';
import { renderPhongFill } from '../render/renderPhongFill';
import {
  DRAPED_GRID_OPACITY,
  toDrapedGridAxis,
  toGridInks,
  toMinorDivisions,
} from '../render/drapedGrid';
import { OUTLINE_OPACITY } from '../render/outlines';
import { parseColor } from '../render/panelShading';
import { toColumnPaints, type ColumnPaints } from '../render/columnPaint';
import {
  SURFACE_PAINT_SIZE,
  toSurfacePaint,
  toUnknownPaintColor,
  type SurfaceColoring,
  type SurfacePaint,
} from '../render/surfacePaint';
import {
  toUnitPoint,
  findSampleAt,
  refreshSampleScreenGeometry,
  renderSampleMarkers,
  renderSampleTooltip,
  sampleScreenPosition,
  type SampleScreenGeometry,
} from '../render/renderSamples';
import { renderSurfaceOverlay } from '../render/renderSurfaceOverlay';
import { renderColumns, renderColumnTooltip } from '../render/renderColumns';
import {
  createSurfaceGpuRenderer,
  requestGpuDevice,
  type SurfaceGpuRenderer,
} from '../render/webgpu/surfaceRenderer';

/** Refine until the worst deviation is under this fraction of the field's range. */
const ADAPTIVE_TOLERANCE = 0.005;

/** Cap, so a pathological field cannot refine until it runs out of grid. */
const ADAPTIVE_VERTEX_BUDGET = 6000;

/** Coarsest buffer a drag will fall to, as a fraction of the viewport. Below this the relief
 *  stops being readable, which defeats the point of keeping the orbit responsive.
 *
 *  Clamped against the host's own scale rather than applied to the halved value: a host that has
 *  already dropped to 0.1 did so because the machine could not keep up, and raising it to the
 *  floor for the length of a drag asks that machine for 6.25 times the pixels at exactly the
 *  moment it has least to spare. The floor only ever reduces the reduction. */
const DRAGGING_RASTER_FLOOR = 0.25;

/** How long a wheel zoom goes without a notch before the reader counts as having stopped.
 *
 *  `wheel` reports notches and never the end of a gesture, so nothing else can say. */
const ZOOM_SETTLE_MS = 150;

export type SurfaceChartStyle = {
  showWireframe: boolean;
  /**
   * The axis grid drawn onto the surface at the floor grid's ticks. Regular where the mesh lines are
   * the triangulation, and hidden where the surface hides it, where they draw through.
   */
  showDrapedGrid: boolean;
  /**
   * Ink along the silhouette and wherever a ridge passes in front of the surface behind it. Unlike
   * the wireframe, which draws through the surface, these say which part is in front.
   */
  showOutlines: boolean;
  /**
   * Bands each light's diffuse term is rounded to, so the surface is shaded in a few flat tones;
   * 0 shades smoothly. Meant for a monochrome ramp: over a color ramp, light bands and value bands
   * compete for the same brightness.
   */
  celSteps: number;
  showDropLines: boolean;
  /** Every triangle gets its own normal, so faces stay hard-edged. */
  faceted: boolean;
  meshMode: 'uniform' | 'adaptive';
  /**
   * Mark where components actually sit.
   *
   * Everything between the markers is interpolated, and on a real estate that is most of the
   * surface. Without them a confidently shaded landscape gives no sign of which parts were measured.
   */
  showSamplePositions: boolean;
  /**
   * The floor and the two back walls, ruled and outlined.
   *
   * Without them the surface floats: height is readable only against a vertical axis standing on
   * its own at the far corner, and position in the plane only against a floor grid the relief
   * hides. The walls give both a plane to be read against.
   */
  showAxisBox: boolean;
  /**
   * The three axis-parallel lines through the hovered point, each spanning the box.
   *
   * Follows `showSamplePositions`, not `showAxisBox`. Hover is resolved against the markers' own
   * screen positions, so `updateHover` refuses to resolve anything while they are hidden and there is
   * no hovered point left for this to draw. Making it independent would mean hovering markers that
   * are not there.
   */
  showCrosshair: boolean;
  shading: number;
  opacity: number;
};

export const DEFAULT_SURFACE_CHART_STYLE: SurfaceChartStyle = {
  showWireframe: false,
  showDrapedGrid: false,
  showOutlines: false,
  celSteps: 0,
  showDropLines: false,
  faceted: false,
  meshMode: 'adaptive',
  showSamplePositions: true,
  showAxisBox: true,
  showCrosshair: true,
  shading: 1,
  opacity: 1,
};

export type SurfaceChartInputs = {
  points: XyzDataPoint[];
  /**
   * The two horizontal axes, decided by the host.
   *
   * Handed in rather than derived, so the four XYZ views can be made to agree about the plane
   * they share. The height axis stays here, because it spans the *interpolated* values and so
   * does not exist until the lattice is built - see `HorizontalAxes`.
   */
  axes: HorizontalAxes;
  interpolation: ZInterpolation;
  aggregation: CoincidentAggregation;
  resolution: GridResolution;
  ramp: SequentialRampName;
  bands: number;
  /** What colors the surface; the ramp and `bands` apply only when it is the height. */
  coloring: SurfaceColoring;
  contourCount: number;
  style: SurfaceChartStyle;
  camera: CameraState;
  lighting: LightingModel;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  surfaceColor: string;
  inkColor: string;
  gridColor: string;
  /**
   * The axis box's faces: a surface one step off `surfaceColor`.
   *
   * Its own colour rather than a wash of `gridColor`, because the walls have to read as panels
   * standing behind the data against whatever is behind them - and which direction "one step off"
   * goes is a question for the theme, not for an alpha. `surfaceDefaultSubtle` is the token that
   * answers it: a shade lighter on a dark surface, a shade darker on a light one.
   */
  panelColor: string;
  format: (value: number) => string;
  /** What each axis's positions mean, numbers unless a host says categories. */
  axisScales: AxisScales;
  /**
   * The height axis the host decided, in place of the round numbers the chart picks for itself -
   * a list plotted as height, whose ticks belong on its values. Null leaves it to the chart.
   */
  heightAxis: HeightAxis | null;
  /** Fraction of the viewport the software path rasterises at. Ignored on the GPU path. */
  rasterScale: number;
  /** 'auto' prefers the GPU and falls back on its own. */
  renderer: 'auto' | 'software' | 'gpu';
  /** Notified when the hovered position changes, so a host can drive a details panel. */
  onHoverSample?: (sample: CollapsedSample | null) => void;
  /**
   * The id of a sample or column to light as if it were hovered, from outside the canvas.
   *
   * Ids are `toSampleId` for a surface position and `toColumnId` for a column. Independent of the
   * pointer: a hover wins while there is one, and this takes over when there is not.
   */
  litId: string | null;
  /** Notified when a press that did not travel releases over a sample or a column. */
  onClickSample?: (sample: CollapsedSample) => void;
  /** Notified with what the chart can name, whenever the data it was built from changes. */
  onMarksChange?: (marks: SurfaceMarks) => void;
  /**
   * Notified when the chart gains or loses something to draw.
   *
   * Not the same question as "are there points". A field pair that puts every component on one
   * line yields plenty of points and no surface, and only the controller knows that.
   */
  onDrawableChange?: (isDrawable: boolean) => void;
};

export type SurfaceMarks =
  | { kind: 'samples'; samples: CollapsedSample[] }
  | { kind: 'columns'; columns: BinnedColumn[] };

const CLICK_TRAVEL_LIMIT = 4;

/** A height axis a host decided: its range and how many divisions it is ruled in. */

const isSameHeightAxis = (
  a: HeightAxis | null,
  b: HeightAxis | null
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.tickCount === b.tickCount &&
    a.range[0] === b.range[0] &&
    a.range[1] === b.range[1]);

export const DEFAULT_SURFACE_CHART_INPUTS: SurfaceChartInputs = {
  points: [],
  axes: {
    x: { range: [0, 1], tickCount: DEFAULT_AXIS_TICK_COUNT, padding: [0, 0] },
    y: { range: [0, 1], tickCount: DEFAULT_AXIS_TICK_COUNT, padding: [0, 0] },
  },
  interpolation: 'linear',
  aggregation: 'mean',
  resolution: 64,
  ramp: 'blue',
  bands: 0,
  coloring: { kind: 'height' },
  contourCount: 0,
  style: DEFAULT_SURFACE_CHART_STYLE,
  camera: DEFAULT_CAMERA,
  lighting: DEFAULT_LIGHTING,
  xTitle: '',
  yTitle: '',
  zTitle: '',
  surfaceColor: '#ffffff',
  inkColor: '#1f2126',
  gridColor: '#dadada',
  panelColor: '#fafafa',
  format: value => String(Math.round(value)),
  axisScales: NUMBER_AXIS_SCALES,
  heightAxis: null,
  rasterScale: 1,
  renderer: 'auto',
  litId: null,
};

/** Cleared transparent so only the surface is opaque and the plane layer shows through. */
const TRANSPARENT_CLEAR = { r: 0, g: 0, b: 0, a: 0 };

const LAYER_CSS =
  /* `outline-offset` rather than `outline:none`: the chrome layer is focusable and keyboard
     operable, so it has to show when it is focused. Inset because the layers fill the container
     and a ring drawn outside the border box would be clipped. */
  'position:absolute;inset:0;display:none;touch-action:none;outline-offset:-2px';

/**
 * One stacked canvas, unattached and hidden.
 *
 * Hidden until `setDrawable` says otherwise, because that only runs inside `render` and `render`
 * returns before it while the renderer is still being detected - an `requestAdapter` and a
 * `requestDevice`, not a frame. Created visible, three absolutely-positioned canvases covered the
 * in-flow empty state for that whole window and swallowed every click on it, with the interactive
 * layer a tab stop announcing a chart that had not been drawn. Every reader meets it, since the
 * view opens with no fields chosen.
 *
 * The interactive layer is the only one that reaches the tab order, and it announces itself as an
 * image: a bare focusable `<canvas>` is a stop that says nothing about what it is or that the
 * arrow keys will do anything. `setInputs` replaces the placeholder label once it knows the axes.
 */
const createLayer = (interactive: boolean): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.setAttribute('style', LAYER_CSS);
  if (interactive) {
    canvas.tabIndex = 0;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', '3D surface chart');
    /* `grab` at rest, so the affordance exists before the reader has tried it. Set here rather
       than left to the gesture handler: nothing dispatches on hover, so a cursor written only on
       a gesture is the UA default until the chart has already been pressed - and on a canvas with
       no other cue that it turns, the cursor is the cue. */
    canvas.style.cursor = 'grab';
  }
  return canvas;
};

/**
 * What a screen reader hears on the chart canvas.
 *
 * Names the three fields rather than only the chart type, because on a chart whose axes are chosen
 * per view "surface chart" identifies nothing, and mentions the keyboard path because nothing else
 * on the element hints that it is more than a picture.
 */
const describeChart = ({
  xTitle,
  yTitle,
  zTitle,
  interpolation,
}: SurfaceChartInputs): string =>
  interpolation === 'binned'
    ? `3D column chart. ${xTitle} across, ${yTitle} deep, ${zTitle} as column height. Use the arrow keys to rotate and plus or minus to zoom.`
    : `3D surface chart. ${xTitle} across, ${yTitle} deep, ${zTitle} as height. Use the arrow keys to rotate and plus or minus to zoom.`;

/**
 * Whether the chart draws one column per occupied bin instead of a surface.
 *
 * Columns are painted in canvas 2D on both renderers: the WebGPU path draws no columns, and in
 * binned mode it is handed an empty mesh.
 */
const drawsColumns = ({
  interpolation,
}: Pick<SurfaceChartInputs, 'interpolation'>): boolean =>
  interpolation === 'binned';

/** Whether the sample markers are drawn: when asked for, and never over columns. */
export const drawsSampleMarkers = (
  inputs: Pick<SurfaceChartInputs, 'interpolation' | 'style'>
): boolean => inputs.style.showSamplePositions && !drawsColumns(inputs);

/**
 * Whether two input sets describe the same picture.
 *
 * Enumerated dynamically rather than by hand. The comparisons above this decide *what to
 * recompute* and can afford to name their fields; this one decides whether to paint at all, and a
 * field missing from a hand-written list would not cost a wasted frame - it would leave the chart
 * showing the old one. Iterating the keys means a new input is covered the day it is added.
 *
 * `style` and `camera` are compared by value because both are routinely rebuilt inline by a
 * caller; `lighting`, `points` and `format` are not, so they stay reference comparisons.
 */
/**
 * Inputs that carry no part of the picture.
 *
 * They reach the comparison because it walks the type rather than a hand-written list - which is
 * the property worth keeping, since it covers a new *visual* input the day it is added. Callbacks
 * are the exception, and the props most certain to arrive rebuilt on every render, so comparing
 * them by reference is what would put the wasted frame straight back. `format` is a function too
 * and is deliberately absent: it changes the axis labels.
 */
const NON_VISUAL_INPUTS = new Set<keyof SurfaceChartInputs>([
  'onHoverSample',
  'onDrawableChange',
  'onClickSample',
  'onMarksChange',
]);

/**
 * The lattice read against the axes the host chose, which is what everything drawn from it uses.
 *
 * Applied per frame rather than folded in when the grid is built: the axes can change without the
 * lattice changing - a host pinning an axis across a settings change does exactly that - and a
 * grid built with one baked in would keep labelling against a stale one.
 */
const withHostAxes = (grid: ScalarGrid, axes: HorizontalAxes): ScalarGrid => ({
  ...grid,
  xRange: axes.x.range,
  yRange: axes.y.range,
});

const isSameAxis = (a: HorizontalAxis, b: HorizontalAxis): boolean =>
  a.tickCount === b.tickCount &&
  a.padding[0] === b.padding[0] &&
  a.padding[1] === b.padding[1] &&
  a.range[0] === b.range[0] &&
  a.range[1] === b.range[1];

const isSameAxes = (a: HorizontalAxes, b: HorizontalAxes): boolean =>
  isSameAxis(a.x, b.x) && isSameAxis(a.y, b.y);

const isSameInputs = (
  a: SurfaceChartInputs,
  b: SurfaceChartInputs
): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<
    keyof SurfaceChartInputs
  >;

  for (const key of keys) {
    if (NON_VISUAL_INPUTS.has(key)) continue;
    if (key === 'camera') {
      if (!isSameCamera(a.camera, b.camera)) return false;
      continue;
    }
    /* By value, not by identity: `axes` is a host-built object, and a host that assembles it
       inline hands over a new one on every update - which a reference test reads as a change and
       answers with a full repaint, on every frame of an unrelated drag. */
    if (key === 'axes') {
      if (!isSameAxes(a.axes, b.axes)) return false;
      continue;
    }
    if (key === 'resolution') {
      if (!isSameResolution(a.resolution, b.resolution)) return false;
      continue;
    }
    if (key === 'style') {
      const styleKeys = new Set([
        ...Object.keys(a.style),
        ...Object.keys(b.style),
      ]) as Set<keyof SurfaceChartStyle>;
      for (const styleKey of styleKeys) {
        if (a.style[styleKey] !== b.style[styleKey]) return false;
      }
      continue;
    }
    if (a[key] !== b[key]) return false;
  }

  return true;
};

/**
 * Everything the surface chart does, with no framework in it.
 *
 * The React version of this grew to sixteen hook calls and a twenty-one entry dependency array, and
 * that array was the direct cause of two separate bugs: an effect keyed on an object rebuilt every
 * render, which dispatched forever; and a mesh that was never uploaded because the effect owning it
 * could not know the GPU renderer had been created after it ran. A dependency array is a poor way to
 * describe "this expensive thing depends on those three of these twenty inputs", and that is the
 * only thing this class does.
 *
 * Invalidation is explicit instead. `setInputs` compares what changed and marks exactly the stages
 * that follow from it - a new camera repaints, a new field rebuilds the grid, the mesh and the
 * isolines - then schedules one frame. Nothing recomputes because an identity happened to change.
 */
export class SurfaceChartController {
  private readonly plane: HTMLCanvasElement;
  private readonly fill: HTMLCanvasElement;
  private readonly chrome: HTMLCanvasElement;
  private readonly resizeObserver: ResizeObserver;

  private inputs: SurfaceChartInputs = DEFAULT_SURFACE_CHART_INPUTS;
  private viewport: Size = { width: 0, height: 0 };
  /**
   * How many divisions the height axis is drawn with, as `niceAxis` chose them for this data.
   *
   * The z labels and the walls' height rules both have to be drawn with it, or every label sits
   * between two rules. It reaches nothing horizontal: those two are the host's.
   */
  private heightAxisTickCount = DEFAULT_AXIS_TICK_COUNT;
  private axisLabels: AxisLabels = toAxisLabels(
    DEFAULT_SURFACE_CHART_INPUTS.format,
    DEFAULT_SURFACE_CHART_INPUTS.axisScales
  );
  /**
   * Whether the camera is the reader's now, or still the fit's.
   *
   * Not "has the fit run". Latching on the fit itself gets two things wrong, both of which leave
   * the crop this exists to remove: `fitZoom` hands back the camera unchanged when the pane is
   * smaller than its own insets - a container reporting a short size first would have locked
   * zoom 1 forever - and a first observation at a transient size locks a zoom fitted to it, so a
   * pane that starts 200px tall and grows draws the box in a corner of itself.
   *
   * So the fit follows the pane until something else has a claim on the camera, which is the point
   * the argument for latching actually applies from: a reader who has rotated or zoomed does not
   * want their view taken away because they dragged a divider.
   */
  private isCameraOwnedByReader = false;

  private grid: ScalarGrid | null = null;
  private samples: CollapsedSample[] = [];
  private sampleGeometry: SampleScreenGeometry | null = null;
  private hovered: CollapsedSample | null = null;
  private columns: BinnedColumn[] = [];
  private columnBoxes: ColumnBox[] = [];
  private hoveredColumn: BinnedColumn | null = null;
  private samplesById = new Map<string, CollapsedSample>();
  private columnsById = new Map<string, BinnedColumn>();
  private pressedAt: { x: number; y: number } | null = null;
  private mesh: SurfaceMesh = EMPTY_MESH;
  private isolines: Isoline[] = [];
  private colorScale: ColorScale | null = null;

  private device: GPUDevice | null = null;
  /* What WebGPU draws into, never in the document. See `render` for why the visible fill layer
     stays a 2D canvas rather than being handed to WebGPU directly. */
  private gpuTarget: HTMLCanvasElement | null = null;
  /* The in-flight request, not just its result. Two resolutions can overlap - React forwards props
     in a second effect within the constructor's own commit - and each would otherwise ask for its
     own device, with the loser overwritten and unreachable. */
  private devicePromise: Promise<GPUDevice | null> | null = null;
  private gpu: SurfaceGpuRenderer | null = null;
  private rendererKind: 'software' | 'gpu' | null = null;
  /**
   * Whether there is a grid worth painting, or `null` before the first frame has decided.
   *
   * Three states rather than two, so the first answer always applies. Seeded `false` it would
   * match a chart that opens empty, and the branch that hides the layers - which are created
   * visible - would never run.
   */
  private drawable: boolean | null = null;

  private gestures: OrbitGestureState =
    initialOrbitGestureState(DEFAULT_CAMERA);
  /**
   * The camera last painted, and the raster scale it was painted at.
   *
   * The scale rather than whether a gesture was turning. Comparing the flag meant its own
   * transition forced a frame: holding an arrow against the pitch limit turns nothing, but
   * `repeating` latching on the first auto-repeat flipped the comparison, so the surface was
   * re-rasterised at half scale and then back at full for a rotation that never moved. Resolution
   * is only ever worth a frame when it goes up.
   */
  private painted: { camera: CameraState; scale: number } | null = null;
  /** Set by anything that changes the picture other than the camera, which the comparison cannot see. */
  private needsRepaint = true;
  private frame: number | undefined;
  private disposed = false;

  private needsGrid = true;
  /**
   * When the last wheel notch arrived, and whether enough of them have arrived to be a gesture.
   *
   * `null` between gestures. The wheel qualifies from the *second* notch inside `ZOOM_SETTLE_MS`,
   * not the first: a mouse wheel turned deliberately delivers one notch every few hundred
   * milliseconds, and reducing on each would soften and re-sharpen the surface on every one. That
   * is the same distinction `hasTurned` and `repeating` draw for the two rotations.
   */
  private lastWheelAt: number | null = null;
  private wheelSustained = false;
  private wheelSettle: ReturnType<typeof setTimeout> | undefined;
  /**
   * Where the cursor was last seen inside the canvas, or null once it has left.
   *
   * Kept so a rotation that ends without a pointer event still has somewhere to re-resolve the
   * hover. `pointerup` has the release position and uses it; a keyboard rotation has no position
   * of its own, and the surface has turned under a cursor that never moved.
   */
  private lastCursor: { x: number; y: number } | null = null;
  /**
   * The camera the hover was last resolved against.
   *
   * A zoom has no terminator - the wheel reports notches and `+`/`-` never open a gesture - so
   * nothing re-resolved after one, and the marker, the tooltip and `onHoverSample` went on naming
   * the sample the zoom began on. Compared per frame rather than per event, so a trackpad's
   * notches cost one hit-test between paints rather than one each.
   */
  private hoverResolvedAt: { camera: CameraState; viewport: Size } | null =
    null;
  private needsMesh = true;
  private needsNormals = true;
  /** The relief the current normals were built for, so the shading cannot lag the geometry. */
  private normalsScaledFor: number | null = null;
  private needsIsolines = true;
  private needsRamp = true;
  private paint: SurfacePaint | null = null;
  private columnPaints: ColumnPaints | null = null;
  private needsPaint = true;
  private disarmPixelRatio: VoidFunction | null = null;

  constructor(container: HTMLElement) {
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    this.plane = createLayer(false);
    this.fill = createLayer(false);
    /* Only the top layer takes input: it is the one the pointer reaches, and giving the others
       listeners would mean three copies of the same gesture. */
    this.chrome = createLayer(true);
    container.append(this.plane, this.fill, this.chrome);

    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      const next = { width: Math.round(width), height: Math.round(height) };
      if (
        next.width === this.viewport.width &&
        next.height === this.viewport.height
      ) {
        return;
      }
      this.viewport = next;
      this.fitZoomToViewport();
      this.pendRender();
    });
    this.resizeObserver.observe(container);
    this.watchPixelRatio();

    this.attachInteraction();
    /* Detection is asynchronous and nothing waits on it: `rendererKind` stays null until it
       settles, and the first paint happens when it does. */
    this.resolveRenderer().catch(() => this.setRendererKind('software'));
  }

  /**
   * Repaints when the display's density changes, which no resize reports.
   *
   * `devicePixelRatio` changes without the element resizing - dragging a window between a retina
   * and a non-retina display keeps the CSS box identical, so the `ResizeObserver` never fires.
   * Left unwatched the layers keep the backing store they were allocated on the previous display:
   * soft at a third of the pixels the screen can show, or paying nine times the rasterise for
   * pixels nobody sees. `useChartCanvas` watches this for the other three charts; the surface
   * self-corrects on the first orbit, which is what made it easy to leave out.
   *
   * The query is re-armed on each change, because it is written against the ratio in force and
   * stops matching the moment that ratio moves.
   */
  private watchPixelRatio(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const arm = (ratio: number): void => {
      const query = window.matchMedia(`(resolution: ${ratio}dppx)`);
      this.disarmPixelRatio?.();
      const onChange = () => {
        if (this.disposed) return;
        arm(window.devicePixelRatio || 1);
        this.pendRender();
      };
      query.addEventListener('change', onChange);
      this.disarmPixelRatio = () =>
        query.removeEventListener('change', onChange);
    };

    arm(window.devicePixelRatio || 1);
  }

  /**
   * The visible layers flattened into one canvas, or null before anything has been drawn.
   *
   * Export cannot use the layers themselves: cloning a canvas does not carry its pixels, so a
   * DOM-cloning export reads them as blank. Three `drawImage` calls suffice because the WebGPU
   * blit in `render` keeps every visible layer a plain 2D canvas.
   *
   * Sized from a painted layer rather than from `viewport * devicePixelRatio`, and null when no
   * layer has been painted. Both because the layers are the only honest source:
   *
   * - `prepare` re-sizes a layer when `render` runs, so the layers trail a device-pixel-ratio
   *   change by a frame. `watchPixelRatio` schedules that frame — moving the window to a 1x
   *   monitor resizes nothing, so it is the only thing that would — but between the `change`
   *   event and the frame that services it the layers still hold the backing store of the
   *   display they were painted on, and CSS scales it, so the chart looks right while
   *   `window.devicePixelRatio` already reports the new value. An export taken in that window
   *   and sized from the current ratio crops to a quarter, or pads onto white.
   * - `viewport` is set by the `ResizeObserver` on mount, but `render` returns early until
   *   `resolveRenderer` settles. Testing the viewport in that window returns a transparent canvas
   *   that `pngExporter` saves as a blank PNG, rather than the missing-canvas error it reports.
   *
   * `OffscreenCanvas` rather than an element, matching what `ViewContext.createExportCanvas`
   * declares and what Blocks returns: this never belongs in the document.
   */
  createExportCanvas(): OffscreenCanvas | null {
    /* `setDrawable(false)` hides the layers without clearing them, so a grid that has gone
       undrawable leaves the previous surface sitting in them - built from a resolution or a kernel
       that is no longer selected - for export to find. `drawable` is the flag that already knows,
       and its `null` covers the pre-first-frame case for free. */
    if (this.drawable !== true) return null;

    /* Skipping the unpainted ones is also what keeps `drawImage` from throwing on a 0x0 source. */
    const layers = [this.plane, this.fill, this.chrome].filter(
      layer => layer.width > 0 && layer.height > 0
    );
    const [first] = layers;
    if (!first) return null;

    const canvas = new OffscreenCanvas(first.width, first.height);
    const context = canvas.getContext('2d');
    if (!context) return null;

    /* The chart's background is the container's CSS `background`, not anything a layer draws -
       `prepare` ends on `clearRect`, so the layers are transparent everywhere the surface is not.
       Export reads the layers alone, and `pngExporter` composites what it gets onto a hard-coded
       white. In the light theme those agree by coincidence, `surfaceDefault` being `white950`. In the
       dark theme they do not, and a chart that reads near-white on near-black exports as `grey300`
       labels over `#ffffff`, with the halo strokes that should vanish into the background landing as
       dark smudges around every line.
       Filling it here also makes that white inert for this view rather than load-bearing. */
    context.fillStyle = this.inputs.surfaceColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    /* Drawn to the canvas size rather than at natural size, so a layer left at a different
       backing-store size still lands registered with the others. */
    layers.forEach(layer =>
      context.drawImage(layer, 0, 0, canvas.width, canvas.height)
    );

    return canvas;
  }

  /** Which path is drawing, or null while capability detection is still running. */
  getRendererKind(): 'software' | 'gpu' | null {
    return this.rendererKind;
  }

  getCamera(): CameraState {
    return this.gestures.orbit.camera;
  }

  /**
   * The bounds of what is actually drawn, in the projector's coordinates.
   *
   * The walls only reach outward on the two sides meeting the back corner and the plinth hangs
   * below the plane, but which two sides depends on the yaw - so this is the axis-aligned envelope
   * of every corner the box can occupy. A superset by the thickness on two sides, which costs a
   * fraction of a percent of zoom and cannot under-reserve as the camera turns.
   *
   * The bare plane when the box is off, which is all there is to fit then.
   */
  private drawnBounds(): UnitBounds {
    if (!this.inputs.style.showAxisBox) {
      return { x: [-1, 1], y: [-1, 1], z: [0, 1] };
    }
    return axisBoxBounds(this.gestures.orbit.camera.heightScale);
  }

  /**
   * Opens on a zoom that puts the whole box on screen.
   *
   * `DEFAULT_CAMERA.zoom` is 1, which `createProjector` reads as one unit to half the smaller
   * viewport dimension - exactly right for a plane seen straight on, and the plane is never seen
   * straight on. Measured against the drawn box it crops at every pane shape and worst at a square
   * one: 208px off each side at 700x700.
   *
   * Through `clampZoom`, so the opening zoom is one the wheel could have reached. A fit outside the
   * range is a view the reader cannot get back to.
   */
  private fitZoomToViewport(): void {
    if (this.isCameraOwnedByReader) return;
    if (this.viewport.width <= 0 || this.viewport.height <= 0) return;

    const zoom = clampZoom(
      fitZoom({
        camera: this.gestures.orbit.camera,
        viewport: this.viewport,
        bounds: this.drawnBounds(),
        inset: CHROME_INSET,
      })
    );
    if (zoom === this.gestures.orbit.camera.zoom) return;
    const { orbit } = this.gestures;
    this.gestures = {
      ...this.gestures,
      orbit: { ...orbit, camera: { ...orbit.camera, zoom } },
    };
  }

  /**
   * Applies whatever changed and schedules a frame.
   *
   * Every field is compared, and each comparison names exactly the work that follows from it. This
   * is the part a dependency array cannot express: `points` invalidates the grid, the mesh and the
   * contours, while `inkColor` invalidates a repaint and nothing else.
   */
  setInputs(next: Partial<SurfaceChartInputs>): void {
    const previous = this.inputs;
    const merged = { ...previous, ...next };

    if (
      merged.points !== previous.points ||
      merged.interpolation !== previous.interpolation ||
      merged.aggregation !== previous.aggregation ||
      !isSameHeightAxis(merged.heightAxis, previous.heightAxis) ||
      !isSameResolution(merged.resolution, previous.resolution)
    ) {
      this.needsGrid = true;
    }

    /* The axes reach the mesh, not just the labels: `packMesh` normalises a lattice coordinate
       against them through `toLatticeFrame`, so a widened axis moves every vertex. Left out, an
       axis change relabelled the box and left the surface where it was. */
    if (
      this.needsGrid ||
      !isSameAxes(merged.axes, previous.axes) ||
      merged.style.meshMode !== previous.style.meshMode ||
      merged.style.faceted !== previous.style.faceted
    ) {
      this.needsMesh = true;
    }

    /* Separate from the mesh, because the relief is the one camera property the geometry reads and
       it reads it in one place. Marking the whole mesh stale re-triangulated the field on every
       frame of a relief drag to arrive at the positions it already had.

       Decided in `rebuild` against the camera the projector uses, not here against the prop: the
       two diverge whenever a camera arrives mid-gesture, and the normals were scaled for one
       relief while the geometry was projected at another. */

    if (this.needsGrid || merged.contourCount !== previous.contourCount) {
      this.needsIsolines = true;
    }

    if (
      this.needsGrid ||
      merged.ramp !== previous.ramp ||
      merged.bands !== previous.bands
    ) {
      this.needsRamp = true;
    }

    if (
      this.needsGrid ||
      !isSameAxes(merged.axes, previous.axes) ||
      merged.coloring !== previous.coloring ||
      merged.surfaceColor !== previous.surfaceColor
    ) {
      this.needsPaint = true;
    }

    /* An externally supplied camera replaces the orbit's, so a caller can reset the view; a drag in
       progress keeps its own, since otherwise every frame of a drag would be undone by the prop.
       Compared by value: a host that builds its camera inline hands over a new object on every
       update, and a reference test would read that as a reset and discard the user's rotation.

       Held rather than discarded when a gesture is open, and applied when it closes. */
    if (!isSameCamera(merged.camera, previous.camera)) {
      /* A host driving the camera has the same claim on it a reader does, so the pane stops
         refitting: overriding a supplied zoom on the next resize would make the prop look ignored.
         Only an actual change counts - the app supplies a module constant and so never reaches
         here, which is what leaves the fit standing for it. */
      this.isCameraOwnedByReader = true;
      this.gestures = reduceOrbitGesture(this.gestures, {
        type: 'setCamera',
        camera: merged.camera,
      });
    }

    if (
      merged.format !== previous.format ||
      merged.axisScales !== previous.axisScales
    ) {
      this.axisLabels = toAxisLabels(merged.format, merged.axisScales);
    }

    this.inputs = merged;

    /* The one way a hover stops being true without a pointer move: `updateHover` reads this flag
       but only runs from the listener, so hiding the markers with the cursor at rest would leave a
       details panel describing something the chart no longer draws, with no event coming to
       correct it. */
    if (
      !merged.style.showSamplePositions &&
      previous.style.showSamplePositions
    ) {
      this.setHovered(null, 'schedule');
    }

    if (
      merged.xTitle !== previous.xTitle ||
      merged.yTitle !== previous.yTitle ||
      merged.zTitle !== previous.zTitle ||
      merged.interpolation !== previous.interpolation
    ) {
      this.chrome.setAttribute('aria-label', describeChart(merged));
    }

    /* Assigned above first: resolution reads `this.inputs`. Re-resolved rather than resolved once
       in the constructor, where the input is still the default - the whole point of the prop is
       that a support case can force the software path, which needs it to be honoured late. */
    if (merged.renderer !== previous.renderer) {
      this.resolveRenderer().catch(() => this.setRendererKind('software'));
    }

    /* Only when something actually differs. Every comparison above decides what to recompute, and
       then repainting regardless undoes the point of them: a host writing the natural thing -
       `style={{ showWireframe: true }}`, `format={v => formatNumber(v)}` - hands over new object
       identities on every parent render, so an unrelated store tick would cost a full frame. That
       is 0.7 ms on the GPU path and the 49 ms rasterise on the software one, for an identical
       picture. `drawable === null` means no frame has decided anything yet, so the first call
       always paints. */
    if (this.drawable === null || !isSameInputs(previous, merged)) {
      this.pendRender();
    }
  }

  destroy(): void {
    this.disposed = true;
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    if (this.wheelSettle !== undefined) clearTimeout(this.wheelSettle);
    this.wheelSettle = undefined;
    this.resizeObserver.disconnect();
    this.disarmPixelRatio?.();
    this.disarmPixelRatio = null;
    this.gpu?.destroy();
    this.gpu = null;
    /* Released rather than left to the collector: a device holds driver resources, and a view that
       mounts and unmounts with tab switches would accumulate them. */
    this.device?.destroy();
    this.device = null;
    this.gpuTarget = null;
    for (const canvas of [this.plane, this.fill, this.chrome]) {
      canvas.remove();
    }
  }

  /**
   * Shows or hides the three layers, and tells the host which it is.
   *
   * `display: none` rather than a flag the host reads and paints over: the layers are
   * `position: absolute; inset: 0`, so they sit above any in-flow empty state the host renders in
   * the same container regardless of DOM order. Left visible they would swallow every click on it,
   * and the chrome layer would keep its place in the tab order announcing a chart that is not
   * drawn. Hidden, they neither paint, nor take pointer events, nor are focusable.
   */
  private setDrawable(drawable: boolean): void {
    if (this.drawable === drawable) return;
    this.drawable = drawable;
    for (const canvas of [this.plane, this.fill, this.chrome]) {
      canvas.style.display = drawable ? 'block' : 'none';
    }
    this.inputs.onDrawableChange?.(drawable);
  }

  /**
   * Switches which path draws the fill.
   *
   * Nothing has to be rebuilt to change paths, because WebGPU never touches the visible layer: it
   * draws into `gpuTarget` and the result is blitted in. A canvas keeps whichever context it was
   * first given for the rest of its life, so handing the fill layer to WebGPU would make it
   * permanently unable to return a `2d` context - and a switch back would have to replace the
   * element.
   */
  private setRendererKind(kind: 'software' | 'gpu'): void {
    if (this.rendererKind === kind) return;

    /* Released on the way out. `rebuild` uploads to whatever renderer exists, and `setMesh`
       reallocates both buffers whenever the topology changes, so a retained renderer would keep
       churning GPU memory for a path that is no longer drawing. The device is kept: that is the expensive thing to reacquire, and
       `ensureGpuRenderer` rebuilds the rest on the way back. */
    if (kind === 'software') {
      this.gpu?.destroy();
      this.gpu = null;
    }

    this.rendererKind = kind;
    this.pendRender();
  }

  /**
   * Falls back to software if the device is lost after it was acquired.
   *
   * `requestGpuDevice` covers every failure at acquisition, but a device can go at any point after
   * that - a driver reset, a GPU-process crash, a device reclaimed from a backgrounded tab - and
   * the failure is silent rather than loud: work submitted to a lost device is dropped without
   * throwing, `getCurrentTexture` still hands one back, and the blit copies a transparent texture
   * onto the fill layer. The plane and chrome keep painting, so a viewer sees the axes, the
   * wireframe and the markers with no surface between them, which reads as a data problem.
   *
   * `devicePromise` is cleared too. Caching it is what stops two overlapping resolutions each
   * asking for a device, and left alone it would hand the dead one back to every retry.
   */
  private watchForDeviceLoss(device: GPUDevice): void {
    device.lost
      .then(({ reason }) => {
        /* `destroyed` is this controller's own `destroy` resolving the same promise. */
        if (this.disposed || reason === 'destroyed') return;
        this.device = null;
        this.devicePromise = null;
        this.setRendererKind('software');
      })
      .catch(() => {
        /* The promise is specified never to reject; swallowed so an exotic implementation cannot
           take the fallback down with it. */
      });
  }

  private async resolveRenderer(): Promise<void> {
    const requested = this.inputs.renderer;
    if (requested === 'software') {
      this.setRendererKind('software');
      return;
    }

    this.devicePromise ??= requestGpuDevice();
    const device = this.device ?? (await this.devicePromise);
    if (this.disposed) {
      device?.destroy();
      return;
    }
    /* Kept before the staleness check rather than after it. A device is the answer to a question
       that does not change, so it is worth holding whichever request produced it - and dropping it
       here would leak it, since nothing else has a reference and `destroy` releases `this.device`.
       That path is the common one, not a rare interleaving: React forwards props in a second effect
       in the same commit, so `renderer: 'software'` always lands while the constructor's own
       detection is still in flight. */
    const isNewDevice = device !== null && device !== this.device;
    this.device = device;
    if (isNewDevice) this.watchForDeviceLoss(device);

    /* The input can have changed again while detection was in flight, and the later request is the
       one the caller is waiting to see honoured. */
    if (this.inputs.renderer !== requested) return;

    /* An explicit `gpu` preference that cannot be honoured still falls back: refusing to draw helps
       nobody, and the caller can read back what it actually got. */
    this.setRendererKind(device ? 'gpu' : 'software');
  }

  /**
   * Schedules a frame.
   *
   * Guarded against double scheduling, so a burst of pointer moves inside one frame produces one
   * paint rather than one per move.
   *
   * `cameraOnly` says the request came from a camera gesture and nothing else, which is the only
   * case `render` can decide to skip by comparison. Every other caller is changing something the
   * comparison cannot see - the data, the viewport, the renderer, the hovered sample - and marks
   * the frame as owed.
   */
  private pendRender(cameraOnly = false): void {
    if (!cameraOnly) this.needsRepaint = true;
    if (this.disposed || this.frame !== undefined) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.render();
    });
  }

  /**
   * Runs one input through the shared gesture machine and applies what it changed.
   *
   * The camera is the reader's once they turn it, which `hasReaderMovedCamera` records by value -
   * so a press that turns nothing, and a plain click that only focuses the canvas, leave the fit
   * standing.
   */
  private sendGesture(input: OrbitInput): void {
    const previous = this.gestures;
    const next = reduceOrbitGesture(previous, input);
    if (next === previous) return;
    this.gestures = next;

    if (next.hasReaderMovedCamera) this.isCameraOwnedByReader = true;
    if (hasKeyboardRotationStarted(previous, next)) this.endWheelZoom();
    if (next.orbit !== previous.orbit) {
      /* Chrome on the element, not part of the painted picture, so it needs no frame. */
      this.chrome.style.cursor = next.orbit.drag ? 'grabbing' : 'grab';
      this.pendRender(true);
    }
    /* The settle frame for a held `+`/`-`. A tap never dropped the resolution. */
    if (hasKeyboardZoomEnded(previous, next)) this.pendRender(true);
    /* A keyboard rotation turns the surface under a cursor that never moved, and has no release
       position of its own, so the hover is re-resolved at the last position seen - or cleared once
       the cursor has left. */
    if (hasKeyboardRotationEnded(previous, next)) {
      if (this.lastCursor) {
        this.updateHover(this.lastCursor, 'schedule');
        return;
      }
      this.clearHover();
    }
  }

  private attachInteraction(): void {
    const canvas = this.chrome;

    const local = (event: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    };

    canvas.addEventListener('pointerdown', event => {
      if (!isOrbitButton(event)) return;
      this.pressedAt = local(event);
      canvas.setPointerCapture(event.pointerId);
      canvas.focus();
      /* A running wheel countdown is deliberately left alone here; see
         `paints nothing at the moment the reader grabs the chart`. */
      this.sendGesture({
        type: 'pointerDown',
        ...local(event),
        sphere: toCanvasTrackball(canvas.getBoundingClientRect()),
      });
    });
    canvas.addEventListener('pointermove', event => {
      /* Recorded before the drag branch, so a keyboard rotation afterwards re-resolves the hover
         where the cursor is rather than where it was before the drag opened. */
      const cursor = local(event);
      this.lastCursor = cursor;

      if (this.gestures.gesture?.owner === 'pointer') {
        this.sendGesture({ type: 'pointerMove', ...cursor });
        return;
      }
      this.updateHover(cursor, 'schedule');
    });
    canvas.addEventListener('pointerup', event => {
      if (!isOrbitButton(event)) return;
      canvas.releasePointerCapture(event.pointerId);
      this.sendGesture({ type: 'pointerUp' });
      /* Re-resolved at the release position: the hover is frozen for the length of a drag and the
         surface has turned under it. */
      const release = local(event);
      this.lastCursor = release;
      this.updateHover(release, 'schedule');
    });
    canvas.addEventListener('click', event => {
      const pressed = this.pressedAt;
      this.pressedAt = null;
      const target =
        this.hovered ??
        (this.hoveredColumn && toColumnSample(this.hoveredColumn));
      if (!pressed || !target) return;
      const released = local(event);
      const travelled = Math.hypot(
        released.x - pressed.x,
        released.y - pressed.y
      );
      if (travelled > CLICK_TRAVEL_LIMIT) return;
      this.inputs.onClickSample?.(target);
    });
    /* A pointer the browser or OS cancels never produces a `pointerup`. The browser has taken the
       pointer for a gesture of its own, so there is no position left to hit-test. */
    canvas.addEventListener('pointercancel', () => {
      this.lastCursor = null;
      this.sendGesture({ type: 'pointerCancel' });
      this.clearHover();
    });
    canvas.addEventListener('pointerleave', () => {
      this.lastCursor = null;
      this.clearHover();
    });

    /* Bound natively and non-passively. React binds `wheel` passively, so `preventDefault` in an
       `onWheel` prop is ignored and the chart zooms while the page scrolls underneath it. */
    canvas.addEventListener(
      'wheel',
      event => {
        const input = toWheelInput(event);
        if (!input) return;
        event.preventDefault();
        this.noteWheelNotch();
        this.sendGesture(input);
      },
      { passive: false }
    );

    canvas.addEventListener('keydown', event => {
      const input = toKeyDownInput(event);
      if (!input) return;
      event.preventDefault();
      this.sendGesture(input);
    });
    canvas.addEventListener('keyup', event =>
      this.sendGesture(toKeyUpInput(event))
    );
    canvas.addEventListener('blur', () => this.sendGesture({ type: 'blur' }));
  }

  /**
   * The fraction of the viewport the software rasteriser will cover this frame.
   *
   * Halved while a rotation or a zoom is sustained, which is what `RasterizeMeshArgs.scale` was
   * documented for and what nothing was doing: per-pixel shading in JavaScript costs about 49 ms a
   * frame at full scale, and a camera gesture only has to stay legible while it moves. Floored, so
   * a host that has already reduced the scale is not halved again into a buffer too coarse to read.
   *
   * Read by the frame gate as well as by the paint, so the two cannot disagree about what a frame
   * would cost - which is what let the gate schedule a frame purely to make one coarser.
   */
  private toRasterScale(): number {
    const { rasterScale } = this.inputs;
    /* Only the software path pays for pixels. On the GPU the fill costs 0.7 ms whatever the scale,
       so reducing buys nothing - and recording the reduced value in `painted` made the terminator
       raise it again and let one bit-identical frame through per gesture. */
    if (this.rendererKind === 'gpu') return rasterScale;
    if (!this.isSustainedRotation() && !this.isSustainedZoom()) {
      return rasterScale;
    }
    return Math.min(
      rasterScale,
      Math.max(DRAGGING_RASTER_FLOOR, rasterScale / 2)
    );
  }

  /**
   * Whether the rotation is sustained enough to be worth the coarse buffer.
   *
   * A pointer drag qualifies once it has moved; a keyboard one only once the key has begun
   * repeating - a tap is how a reader aims, and drawing one step at half scale and then again
   * sharp is two passes and a visible soften for a nudge.
   */
  private isSustainedRotation(): boolean {
    return isSustainedRotation(this.gestures);
  }

  /**
   * Whether the zoom is sustained enough to be worth the coarse buffer.
   *
   * A held `+`/`-` qualifies on the browser's `repeat` flag; a wheel from the second notch of a
   * run, having no end event to read.
   */
  private isSustainedZoom(): boolean {
    return this.gestures.zoomRepeating || this.wheelSustained;
  }

  /**
   * Records a wheel notch, and restarts the countdown that decides the gesture has ended.
   */
  private noteWheelNotch(): void {
    const previous = this.lastWheelAt;
    const now = performance.now();
    this.lastWheelAt = now;
    if (previous !== null && now - previous <= ZOOM_SETTLE_MS) {
      this.wheelSustained = true;
    }
    if (this.wheelSettle !== undefined) clearTimeout(this.wheelSettle);
    this.wheelSettle = setTimeout(() => this.endWheelZoom(), ZOOM_SETTLE_MS);
  }

  /**
   * Closes the wheel zoom and asks the gate for a settle frame.
   */
  private endWheelZoom(): void {
    if (this.wheelSettle !== undefined) clearTimeout(this.wheelSettle);
    this.wheelSettle = undefined;
    this.lastWheelAt = null;
    if (!this.wheelSustained) return;
    this.wheelSustained = false;
    this.pendRender(true);
  }

  /**
   * Records the hovered sample, and whether the picture still has to be drawn for it.
   *
   * `'schedule'` from a listener, where the frame this asks for is the one that draws the new
   * hover. `'drawing'` from inside `render`, where it is not: that frame paints the markers from
   * this value forty lines later, and `pendRender` there sets `needsRepaint` back to true twelve
   * lines after the frame cleared it - so an identical picture was drawn again, at 49 ms on the
   * software path, once per gesture that moved a different sample under a resting cursor.
   */
  private setHovered(
    sample: CollapsedSample | null,
    when: 'schedule' | 'drawing'
  ): void {
    if (this.hovered === sample) return;
    this.hovered = sample;
    this.inputs.onHoverSample?.(sample);
    if (when === 'schedule') this.pendRender();
  }

  private clearHover(): void {
    this.setHovered(null, 'schedule');
    this.setHoveredColumn(null, 'schedule');
  }

  private setHoveredColumn(
    column: BinnedColumn | null,
    when: 'schedule' | 'drawing'
  ): void {
    if (this.hoveredColumn === column) return;
    this.hoveredColumn = column;
    this.inputs.onHoverSample?.(column && toColumnSample(column));
    if (when === 'schedule') this.pendRender();
  }

  private toColumnOrder(grid: ScalarGrid): ColumnBox[] {
    return toPaintingOrder(
      this.columnBoxes,
      toViewer(this.gestures.orbit.camera),
      toLatticeFrame(grid)
    );
  }

  private toShownSample(): CollapsedSample | null {
    if (this.hovered) return this.hovered;
    const { litId, style } = this.inputs;
    if (
      litId === null ||
      !style.showSamplePositions ||
      drawsColumns(this.inputs)
    )
      return null;
    return this.samplesById.get(litId) ?? null;
  }

  private toShownColumn(): BinnedColumn | null {
    if (this.hoveredColumn) return this.hoveredColumn;
    const { litId } = this.inputs;
    if (litId === null || !drawsColumns(this.inputs)) return null;
    return this.columnsById.get(litId) ?? null;
  }

  private updateHover(
    cursor: { x: number; y: number },
    when: 'schedule' | 'drawing'
  ): void {
    /* Recorded wherever the hover is resolved from, not only in the per-frame check, or that check
       would resolve again on the next frame for a camera a terminator had already answered for. */
    this.hoverResolvedAt = {
      camera: this.gestures.orbit.camera,
      viewport: this.viewport,
    };
    if (this.grid && drawsColumns(this.inputs)) {
      const { camera } = this.gestures.orbit;
      this.setHoveredColumn(
        findColumnAt({
          order: this.toColumnOrder(withHostAxes(this.grid, this.inputs.axes)),
          viewer: toViewer(camera),
          project: createProjector(camera, this.viewport),
          cursor,
        }),
        when
      );
      return;
    }
    if (!this.grid || !this.inputs.style.showSamplePositions) {
      this.setHovered(null, when);
      return;
    }
    /* Against the host's axes, because that is what the markers were drawn against. Hit-testing
       the sampling domain instead would answer for a position the mark is not at, by whatever the
       host widened the axis by. */
    const grid = withHostAxes(this.grid, this.inputs.axes);

    this.setHovered(
      findSampleAt(this.currentSampleGeometry(grid), cursor),
      when
    );
  }

  private currentSampleGeometry(grid: ScalarGrid): SampleScreenGeometry {
    this.sampleGeometry = refreshSampleScreenGeometry(this.sampleGeometry, {
      samples: this.samples,
      grid,
      camera: this.gestures.orbit.camera,
      viewport: this.viewport,
    });
    return this.sampleGeometry;
  }

  /** Rebuilds only the stages marked stale, in dependency order. */
  private rebuild(): void {
    const { inputs } = this;

    if (this.needsGrid) {
      const built = toScalarGrid({
        points: inputs.points,
        interpolation: inputs.interpolation,
        aggregation: inputs.aggregation,
        resolution: inputs.resolution,
      });
      /* The height axis rounded to round numbers, here rather than downstream so the mesh and the
         labels agree: rounded only where the labels are drawn, they would say one thing and the
         surface's own normalisation another, and every tick would sit at the wrong height.

         `dataZRange` deliberately keeps the unrounded range. Rounding outward always widens the
         axis a little, and the two things that span a range without lining up with a gridline lose
         by following it - the colour ramp would spend bands on padding the surface never reaches,
         and a contour threshold above the data maximum draws no isoline at all.

         The division count comes back with the range because the two are one answer; see
         `niceAxis`. The horizontal axes are rounded in the source-data layer, by
         `toNiceHorizontalAxes`. */
      /* `dataZRange`, not `zRange`: the two are equal as `toScalarGrid` builds them, but this is
         the line that *produces* the axis, so reading the axis field to compute it is circular -
         and would follow silently if gridding ever set the two differently. */
      const heightAxis =
        inputs.heightAxis ??
        niceAxis(
          drawsColumns(inputs)
            ? toColumnHeightRange(built.dataZRange)
            : built.dataZRange
        );
      this.heightAxisTickCount = heightAxis.tickCount;
      this.grid = { ...built, zRange: heightAxis.range };
      /* The same collapse the gridding performs, kept so the markers stand exactly where the
         interpolation was anchored rather than at a second, separately-derived set of positions. */
      this.samples = collapseCoincident(inputs.points, inputs.aggregation);
      this.columns = drawsColumns(inputs)
        ? toBinnedColumns(this.grid, this.samples)
        : [];
      this.samplesById = new Map(
        this.samples.map(sample => [toSampleId(sample), sample])
      );
      this.columnsById = new Map(
        this.columns.map(column => [toColumnId(column), column])
      );
      this.setHoveredColumn(null, 'drawing');
      /* Through `setHovered` rather than assigned, so `onHoverSample` hears about it: the samples
         it named belong to the previous dataset, and a host details panel would otherwise keep
         showing one until the pointer happened to cross the canvas again.

         `'drawing'`, because `rebuild` runs inside `render`: the frame that would be scheduled is
         the one already in progress. */
      this.setHovered(null, 'drawing');
      this.inputs.onMarksChange?.(
        drawsColumns(inputs)
          ? { kind: 'columns', columns: this.columns }
          : { kind: 'samples', samples: this.samples }
      );
      this.needsGrid = false;
    }
    if (!this.grid) return;
    const grid = withHostAxes(this.grid, inputs.axes);

    /* Decided here rather than in `setInputs`, and against the camera the projector uses. The two
       diverge whenever a camera arrives mid-gesture, and reading the prop meant the normals were
       scaled for one relief while the geometry was projected at another - permanently, once the
       prop had been dropped. Comparing what the normals were actually built for also means a
       relief that changes and changes back costs nothing. */
    const { heightScale } = this.gestures.orbit.camera;
    if (!this.needsMesh && this.normalsScaledFor !== heightScale) {
      this.needsNormals = true;
    }

    if (this.needsMesh && drawsColumns(inputs)) {
      this.mesh = EMPTY_MESH;
      this.columnBoxes = toColumnBoxes(
        this.columns,
        grid,
        toLatticeFrame(grid)
      );
      this.needsMesh = false;
      this.needsNormals = false;
      this.normalsScaledFor = heightScale;
      this.gpu?.setMesh(this.mesh);
    } else if (this.needsMesh) {
      this.columnBoxes = [];
      const base =
        inputs.style.meshMode === 'adaptive'
          ? toAdaptiveMesh({
              grid,
              heightScale,
              tolerance: ADAPTIVE_TOLERANCE,
              maximumVertices: ADAPTIVE_VERTEX_BUDGET,
            })
          : toUniformMesh(grid, heightScale);
      this.mesh = inputs.style.faceted
        ? toFacetedMesh(base, heightScale)
        : base;
      this.needsMesh = false;
      this.needsNormals = false;
      this.normalsScaledFor = heightScale;
      this.gpu?.setMesh(this.mesh);
    } else if (this.needsNormals && this.mesh) {
      this.mesh = rescaleNormals(this.mesh, heightScale);
      this.needsNormals = false;
      this.normalsScaledFor = heightScale;
      this.gpu?.setMesh(this.mesh);
    }

    if (this.needsIsolines) {
      this.isolines =
        inputs.contourCount > 0 && !drawsColumns(inputs)
          ? toIsolines(grid, toThresholds(grid.dataZRange, inputs.contourCount))
          : [];
      this.needsIsolines = false;
    }

    if (this.needsRamp) {
      this.colorScale = createColorScale({
        range: grid.dataZRange,
        ramp: inputs.ramp,
        bands: inputs.bands,
      });
      this.needsRamp = false;
      this.gpu?.setColorScale(this.colorScale);
    }

    if (this.needsPaint) {
      const unknownColor = toUnknownPaintColor(inputs.surfaceColor);
      this.columnPaints = drawsColumns(inputs)
        ? toColumnPaints({
            coloring: inputs.coloring,
            columns: this.columns,
            unknownColor,
          })
        : null;
      this.paint = drawsColumns(inputs)
        ? null
        : toSurfacePaint({
            coloring: inputs.coloring,
            points: inputs.points,
            grid,
            interpolation: inputs.interpolation,
            resolution: inputs.resolution,
            overfillForClipping: false,
            unknownColor,
            size: SURFACE_PAINT_SIZE,
          });
      this.needsPaint = false;
      this.gpu?.setPaint(this.paint);
    }
  }

  /** Sizes a 2D layer at device resolution and clears it, returning a context ready to paint. */
  private prepare(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(this.viewport.width * ratio);
    const height = Math.round(this.viewport.height * ratio);

    /* Assigning width or height reallocates the backing store and clears it, so it is guarded:
       during an orbit the size never changes. */
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    /* Outside the guard, and not left to the `inset:0` the layer is created with. A canvas is a
       replaced element, so an absolutely positioned one resolves `width:auto` to its intrinsic
       width and ignores `right` as over-constrained - `inset` places the box but never sizes it.
       The backing store is therefore the layout size too, which equals the container only at
       ratio 1; above it every layer laid out `ratio` times too large and the chart drew magnified
       past the container's clip. */
    canvas.style.width = `${this.viewport.width}px`;
    canvas.style.height = `${this.viewport.height}px`;

    const context = canvas.getContext('2d');
    if (!context) return null;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, this.viewport.width, this.viewport.height);
    return context;
  }

  private ensureGpuRenderer(): void {
    if (this.gpu || this.rendererKind !== 'gpu' || !this.device) return;
    if (!this.colorScale) return;

    this.gpuTarget ??= document.createElement('canvas');
    this.gpu = createSurfaceGpuRenderer({
      device: this.device,
      canvas: this.gpuTarget,
      colorScale: this.colorScale,
    });
    /* Uploaded on creation as well as on change: detection is asynchronous, so the mesh is usually
       already built by the time there is a renderer to hand it to. */
    this.gpu.setMesh(this.mesh);
    this.gpu.setPaint(this.paint);
  }

  private render(): void {
    if (this.disposed || !this.rendererKind) return;
    if (this.viewport.width === 0 || this.viewport.height === 0) return;

    /* Compared here rather than at dispatch, because what the frame draws is decided when the
       frame runs. A move the pitch clamp absorbs advances the drag anchor - it has to, or the
       gesture would jump coming back off the limit - so the state object legitimately changes
       while the picture does not, and only the camera and whether a drag is open reach the paint. */
    /* The resolution the frame would land at, not whether a button is down. A press with no
       motion draws the same picture at the same resolution as no press at all, and comparing the
       press made the drag-presence flip guarantee a frame.

       Compared as an inequality rather than for equality, which is the part that took two goes:
       a frame is worth drawing when it would be *sharper* than what is on screen, and never when
       it would only be coarser. Equality re-rasterised the surface at half scale the moment
       `repeating` latched, even against the pitch clamp where nothing had moved, and again at full
       scale on release - the soften-and-sharpen this reduction exists to avoid, reached from the
       flag instead of from the terminator. */
    const rasterScale = this.toRasterScale();
    if (
      !this.needsRepaint &&
      this.painted !== null &&
      isSameCamera(this.painted.camera, this.gestures.orbit.camera) &&
      rasterScale <= this.painted.scale
    ) {
      return;
    }
    this.needsRepaint = false;

    this.rebuild();
    const { colorScale, inputs, viewport } = this;
    /* The host's axes applied here, where the frame is actually drawn from. `rebuild` has its own
       binding for the mesh, and applying them only there reached nothing that is drawn: the box
       went on carrying the extent while the contour beside it, off the same source data, carried
       the host's axis. */
    const grid = this.grid && withHostAxes(this.grid, inputs.axes);
    /* Reported rather than only returned on. `toScalarGrid` hands back an empty grid for fewer
       than three distinct positions, for collinear ones, and when no cell fills - all of which
       leave points to count but nothing to draw, so a host keying its empty state on
       `points.length` would show a blank panel and no explanation. */
    if (!grid || !colorScale || grid.width < 2) {
      this.setDrawable(false);
      return;
    }
    /* Recorded here rather than beside the gate, because a frame that bailed on an undrawable grid
       painted nothing and should not claim to have. Benign until it is not: every route back to a
       drawable grid happens to set `needsRepaint`, so the stale record was never consulted - and
       that is a property of the routes rather than of this. */
    this.painted = { camera: this.gestures.orbit.camera, scale: rasterScale };
    this.setDrawable(true);
    const shownSample = this.toShownSample();
    const shownColumn = this.toShownColumn();

    /* Re-resolved here, once per frame, for the camera changes no terminator covers: a wheel
       reports notches and never an end, and `+`/`-` never open a gesture to close. Skipped while
       one is in progress, where the freeze is deliberate and the terminator does it - so this is
       the zoom case and nothing else. Ordered after `setDrawable` because `updateHover` needs the
       grid this frame rebuilt. */
    /* The viewport as well as the camera: the sample screen geometry reads both, so a resize
       moves every sample exactly as a zoom does and the hover would go on naming the old one. */
    if (
      !isRotating(this.gestures) &&
      this.lastCursor &&
      (this.hoverResolvedAt === null ||
        !isSameCamera(
          this.hoverResolvedAt.camera,
          this.gestures.orbit.camera
        ) ||
        this.hoverResolvedAt.viewport !== this.viewport)
    ) {
      this.updateHover(this.lastCursor, 'drawing');
    }

    /* Falling back rather than propagating. This runs inside a `requestAnimationFrame` callback
       and before the plane is painted, so an escaping throw leaves the frame drawing nothing at
       all - not the axes, not the markers, not the software surface - against layers already made
       visible, and repeats on every frame after it. `createSurfaceGpuRenderer` throws outright
       when `getContext('webgpu')` returns null, which a page holding more GPU canvases than the
       compositor will hand out reaches. Every other GPU failure here already resolves to
       software. */
    try {
      this.ensureGpuRenderer();
    } catch {
      this.gpuTarget = null;
      this.setRendererKind('software');
    }

    const chrome = {
      camera: this.gestures.orbit.camera,
      viewport,
      xTitle: inputs.xTitle,
      yTitle: inputs.yTitle,
      zTitle: inputs.zTitle,
      xRange: grid.xRange,
      yRange: grid.yRange,
      zRange: grid.zRange,
      axisLabels: this.axisLabels,
      inkColor: inputs.inkColor,
      surfaceColor: inputs.surfaceColor,
      xTickCount: inputs.axes.x.tickCount,
      yTickCount: inputs.axes.y.tickCount,
      zTickCount: this.heightAxisTickCount,
      xPadding: inputs.axes.x.padding,
      yPadding: inputs.axes.y.padding,
      /* The horizontal tick labels hang off the plane's front edge, which is where the plinth hangs
         from too - so they clear the slab rather than sitting on it. Zero when the box is off. */
      boxDepth: inputs.style.showAxisBox
        ? axisBoxBase(
            AXIS_BOX_THICKNESS,
            this.gestures.orbit.camera.heightScale
          )
        : 0,
      /* So `renderAxes3d` leaves the vertical axis to the box, which draws that segment as the
         edge its two walls share. */
      hasAxisBox: inputs.style.showAxisBox,
    };

    /* Everything the box needs but the context, which differs per pass - the plinth is drawn on the
       chrome layer and the rest on the plane layer. */
    const box = {
      camera: this.gestures.orbit.camera,
      viewport,
      inkColor: inputs.inkColor,
      panelColor: inputs.panelColor,
      xTickCount: chrome.xTickCount,
      yTickCount: chrome.yTickCount,
      zTickCount: chrome.zTickCount,
      xTickInset: toTickInset(inputs.axes.x),
      yTickInset: toTickInset(inputs.axes.y),
    };

    const planeContext = this.prepare(this.plane);
    if (planeContext) {
      /* The box straddles the axes. Its planes go under them, so the floor grid and the vertical
         axis are ruled on the panel rather than beneath it; its thickness goes over them, because a
         face standing in front of the floor cannot have the floor's ruling showing through it. */
      if (inputs.style.showAxisBox) {
        renderAxisBox({ ...box, context: planeContext, pass: 'planes' });
      }
      renderAxes3d({ ...chrome, context: planeContext, pass: 'plane' });
      if (inputs.style.showAxisBox) {
        renderAxisBox({
          ...box,
          context: planeContext,
          pass: 'wallThickness',
        });
        /* The plinth's underside here rather than with the rest of it, on the layer beneath the
           mesh. It is the one face of the box that spans the whole footprint, and it is visible
           exactly when the camera is under the plane - where the floor is culled and it would
           otherwise be filled opaquely over the surface, the wireframe and the isolines, leaving a
           flat panel where the chart was. Painted before the data, as the wall rims are, so the
           surface draws over it. */
        renderAxisBox({
          ...box,
          context: planeContext,
          pass: 'plinthBase',
        });
      }
      renderAxes3d({ ...chrome, context: planeContext, pass: 'heightLabels' });
    }

    const fillContext = this.prepare(this.fill);

    const columnOrder = drawsColumns(inputs) ? this.toColumnOrder(grid) : [];
    const gridInk = parseColor(inputs.inkColor);
    const gridSurface = parseColor(inputs.surfaceColor);
    const drapedGrid =
      inputs.style.showDrapedGrid && gridInk && gridSurface
        ? {
            x: toDrapedGridAxis(
              inputs.axes.x.tickCount,
              toTickInset(inputs.axes.x),
              toMinorDivisions(toTickStep(inputs.axes.x))
            ),
            y: toDrapedGridAxis(
              inputs.axes.y.tickCount,
              toTickInset(inputs.axes.y),
              toMinorDivisions(toTickStep(inputs.axes.y))
            ),
            ...toGridInks(gridInk, gridSurface),
            opacity: DRAPED_GRID_OPACITY,
          }
        : null;
    const outlineColor = inputs.style.showOutlines
      ? parseColor(inputs.inkColor)
      : null;
    const outline = outlineColor
      ? { color: outlineColor, opacity: OUTLINE_OPACITY }
      : null;

    if (drawsColumns(inputs)) {
      if (fillContext) {
        renderColumns({
          context: fillContext,
          order: columnOrder,
          viewer: toViewer(this.gestures.orbit.camera),
          camera: this.gestures.orbit.camera,
          viewport,
          colorScale,
          paints: this.columnPaints,
          lighting: inputs.lighting,
          shading: inputs.style.shading,
          opacity: inputs.style.opacity,
          edgeColor: inputs.style.showWireframe ? inputs.inkColor : null,
          highlighted: shownColumn,
          highlightColor: inputs.inkColor,
        });
      }
    } else if (
      /* `mesh.triangleCount > 0` guards the blit, not the draw. `draw` already declines an empty
         mesh - but declining means submitting no pass, so `gpuTarget` still holds the last frame it
         presented, and copying that onto a freshly cleared fill layer repaints a surface built from
         data or a setting that is no longer selected. The software path gets this free: `rasterizeMesh`
         returns null and the cleared layer stays cleared. An empty mesh over a valid grid is
         reachable - `binned` on a score estate fills isolated cells and no 2x2 block. */
      this.rendererKind === 'gpu' &&
      this.gpu &&
      this.gpuTarget &&
      this.mesh.triangleCount > 0
    ) {
      const ratio = window.devicePixelRatio || 1;
      const width = Math.round(viewport.width * ratio);
      const height = Math.round(viewport.height * ratio);
      if (this.gpuTarget.width !== width || this.gpuTarget.height !== height) {
        this.gpuTarget.width = width;
        this.gpuTarget.height = height;
      }
      this.gpu.draw({
        camera: this.gestures.orbit.camera,
        /* Device pixels, because the swap-chain texture is: the projection has to match the surface
           it draws onto rather than the CSS box. */
        viewport: { width, height },
        lighting: inputs.lighting,
        shading: inputs.style.shading,
        /* The ramp's domain, not the axis: this uniform's only job is to normalise a vertex value
           for the ramp texture lookup, and that texture is baked from `this.colorScale`. Handed
           the rounded range it would divide by a wider span than the texture spans, so the top
           bands would never be sampled and the GPU would disagree with the software renderer about
           colour. Height comes from `position.z`, which is normalised against the axis. */
        zRange: grid.dataZRange,
        clearColor: TRANSPARENT_CLEAR,
        celSteps: inputs.style.celSteps,
        drapedGrid,
        outline,
      });
      /* Blitted into the visible 2D layer rather than drawn straight onto it, in the same task as
         the submit. A WebGPU canvas can only be read back before its frame is presented - one
         `await` and `drawImage` or `toDataURL` returns fully transparent, measured - so a chart
         that rendered directly onto the visible layer would look right and export blank, which is
         how every canvas view ends up with no export at all. Copying it into a 2D canvas here
         leaves something anything can read, at any time, and costs 0.002 ms at 1200x900 against
         the 0.7 ms draw it follows. */
      if (fillContext) {
        fillContext.save();
        /* Applied here rather than in the shader, which is the other payoff from compositing
           through a 2D layer: the software path applies `opacity` as `globalAlpha` too, so the
           two agree by construction instead of by a uniform kept in step across two languages. */
        fillContext.globalAlpha = inputs.style.opacity;
        fillContext.drawImage(
          this.gpuTarget,
          0,
          0,
          viewport.width,
          viewport.height
        );
        fillContext.restore();
      }
    } else if (fillContext) {
      renderPhongFill({
        context: fillContext,
        mesh: this.mesh,
        camera: this.gestures.orbit.camera,
        viewport,
        colorScale,
        lighting: inputs.lighting,
        shading: inputs.style.shading,
        scale: rasterScale,
        opacity: inputs.style.opacity,
        drapedGrid,
        paint: this.paint,
        celSteps: inputs.style.celSteps,
        outline,
      });
    }

    const chromeContext = this.prepare(this.chrome);
    if (chromeContext) {
      renderSurfaceOverlay({
        context: chromeContext,
        mesh: this.mesh,
        grid,
        camera: this.gestures.orbit.camera,
        viewport,
        isolines: this.isolines,
        style: {
          showWireframe: inputs.style.showWireframe,
          showDropLines: inputs.style.showDropLines,
          showIsolines: inputs.contourCount > 0,
          opacity: inputs.style.opacity,
        },
        inkColor: inputs.inkColor,
      });
      /* The plinth after the surface, where drop lines run down to the floor plane on this layer and
         it stands in front of them. Only while the camera is above the plane: below it every plinth
         face projects across the footprint, so `passOf` sends the whole plinth to the pass above
         instead, and this one draws nothing. Before the labels, which belong on top of everything. */
      if (inputs.style.showAxisBox) {
        renderAxisBox({
          ...box,
          context: chromeContext,
          pass: 'plinthThickness',
        });
      }
      renderAxes3d({ ...chrome, context: chromeContext, pass: 'labels' });

      const hoveredBox = columnOrder.find(
        ({ column }) => column === shownColumn
      );
      if (inputs.style.showCrosshair && hoveredBox) {
        renderCrosshair3d({
          context: chromeContext,
          camera: this.gestures.orbit.camera,
          viewport,
          point: [
            (hoveredBox.x[0] + hoveredBox.x[1]) / 2,
            (hoveredBox.y[0] + hoveredBox.y[1]) / 2,
            hoveredBox.column.value < 0 ? hoveredBox.z[0] : hoveredBox.z[1],
          ],
          inkColor: inputs.inkColor,
          surfaceColor: inputs.surfaceColor,
        });
      }
      if (hoveredBox) {
        renderColumnTooltip({
          context: chromeContext,
          box: hoveredBox,
          camera: this.gestures.orbit.camera,
          viewport,
          xTitle: inputs.xTitle,
          yTitle: inputs.yTitle,
          zTitle: inputs.zTitle,
          axisLabels: this.axisLabels,
          inkColor: inputs.inkColor,
          surfaceColor: inputs.surfaceColor,
          gridColor: inputs.gridColor,
        });
      }

      /* Over the surface, and before the markers so the hovered dot sits on top of its own lines.
         `shownSample` is already null whenever the markers are hidden - `updateHover` resolves
         against their screen positions and refuses without them - so this follows
         `showSamplePositions` too, and the flag doc says so. */
      if (inputs.style.showCrosshair && shownSample) {
        renderCrosshair3d({
          context: chromeContext,
          camera: this.gestures.orbit.camera,
          viewport,
          point: toUnitPoint(grid, shownSample),
          inkColor: inputs.inkColor,
          surfaceColor: inputs.surfaceColor,
        });
      }

      if (drawsSampleMarkers(inputs)) {
        const geometry = this.currentSampleGeometry(grid);
        renderSampleMarkers({
          context: chromeContext,
          geometry,
          hovered: shownSample,
          inkColor: inputs.inkColor,
          surfaceColor: inputs.surfaceColor,
          opacity: inputs.style.opacity,
        });

        /* Last of everything, so the tooltip is never drawn under a label or a contour. */
        const hoveredAt =
          shownSample && sampleScreenPosition(geometry, shownSample);
        if (shownSample && hoveredAt) {
          renderSampleTooltip({
            context: chromeContext,
            sample: shownSample,
            at: hoveredAt,
            viewport,
            xTitle: inputs.xTitle,
            yTitle: inputs.yTitle,
            zTitle: inputs.zTitle,
            axisLabels: this.axisLabels,
            inkColor: inputs.inkColor,
            surfaceColor: inputs.surfaceColor,
            gridColor: inputs.gridColor,
          });
        }
      }
    }
  }
}
